import { CustomerService } from "@/constants/base"
import type { FeatureId } from "@/theme"
import { AlertDialogBodyText, Box, Column, TextWithIcon } from "@/theme"
import i18n from "@/translations/i18n"

export interface HelpContentStatic {
  bodyElement: React.JSX.Element | string
  titleElement?: React.JSX.Element | string
}

export type HelpContentProp =
  | HelpContentStatic
  | keyof typeof HELP_CONTENT_MAP
  | null
  | undefined

export interface HelpContentProps {
  content: HelpContentProp
}

type HelpContentMap = {
  [key in FeatureId]: HelpContentStatic | null
}
export const HELP_CONTENT_MAP = {
  activateField: null,
  addDevice: {
    bodyElement: i18n.t("codaDeviceAliasHelpText", { ns: "addDevice" }),
    titleElement: i18n.t("codaDeviceAliasHelpTitle", { ns: "addDevice" }),
  },
  addPhoneNumber: {
    bodyElement: i18n.t("addPhoneNumberHelpBodyText", { ns: "phoneNumbers" }),
    titleElement: i18n.t("addPhoneNumberHelpTitle", { ns: "phoneNumbers" }),
  },
  addSensor: null,
  adminLanding: null,
  analyticsExternal: null,
  appNotifications: null,
  appPreferences: null,
  archiveField: {
    bodyElement: i18n.t("fields:archiveFieldsHelpDescription"),
    titleElement: i18n.t("fields:archiveFieldTitle"),
  },
  changeFarmAccount: {
    bodyElement: i18n.t("farmAccountHelpBodyText", { ns: "noFarms" }),
    titleElement: i18n.t("changeFarmAccount"),
  },
  confirmSignUp: null,
  contactUs: {
    bodyElement: i18n.t("support:farmHqIsHereToHelp"),
  },
  createConfiguration: {
    bodyElement: i18n.t("helpContentBodyText", { ns: "deviceConfiguration" }),
    titleElement: i18n.t("title", { ns: "deviceConfiguration" }),
  },
  createDeviceActionSchedule: {
    bodyElement: i18n.t("createScheduleHelpContent", { ns: "createSchedule" }),
    titleElement: i18n.t("createScheduleHelpTitle", { ns: "createSchedule" }),
  },
  createDeviceComment: {
    bodyElement: i18n.t(
      "thisCommentWillBeVisibleToAllOtherUsersAssociatedWithYourFarmAccount",
      { ns: "deviceComments" },
    ),
    titleElement: i18n.t("deviceComment", { ns: "deviceComments" }),
  },
  createDeviceConnection: {
    bodyElement: i18n.t("helpBodyText", { ns: "createDeviceConnection" }),
    titleElement: i18n.t("aboutDeviceConnections", {
      ns: "createDeviceConnection",
    }),
  },

  createDeviceNotificationRule: {
    bodyElement: i18n.t("createNotificationHelpBodyText", {
      ns: "deviceNotifications",
    }),
    titleElement: i18n.t("aboutCustomNotifications", {
      ns: "deviceNotifications",
    }),
  },
  createFarm: {
    bodyElement: i18n.t("farmAccountHelpBodyText", { ns: "noFarms" }),
    titleElement: i18n.t("createFarmHelpTitle", { ns: "noFarms" }),
  },
  createFarmJoinCode: {
    bodyElement: i18n.t("farmJoinCodeHelpSummary", { ns: "farmJoinCodes" }),
    titleElement: i18n.t("farmJoinCodeTitle", { ns: "farmJoinCodes" }),
  },
  createField: {
    bodyElement: i18n.t("createField:helpBodyText"),
    titleElement: i18n.t("createField:helpTitle"),
  },
  createSupportTicket: {
    bodyElement: i18n.t("createSupportTicketHelpContentWithPhoneNumber", {
      ns: "support",
      phoneNumber: CustomerService.PHONE_NUMBER,
    }),
  },
  deactivateFarm: null,
  debugInfo: null,
  deleteAccount: null,
  deleteField: null,
  deviceActionHistory: null,
  deviceCommandConfirmation: null,
  deviceConfiguration: {
    bodyElement: i18n.t("deviceConfiguration:helpContentBodyText"),
    titleElement: i18n.t("deviceConfiguration:title"),
  },
  deviceConnections: {
    bodyElement: i18n.t("helpBodyText", {
      ns: "createDeviceConnection",
    }),

    titleElement: i18n.t("aboutDeviceConnections", {
      ns: "createDeviceConnection",
    }),
  },
  deviceControlPanel: {
    bodyElement: i18n.t("controlPanelHelpContent", { ns: "deviceActions" }),
    titleElement: i18n.t("aboutDeviceControls", { ns: "deviceActions" }),
  },
  deviceAnalytics: null,
  deviceEventHistory: {
    bodyElement: i18n.t("helpContentBodyText", { ns: "deviceEventHistory" }),
  },
  deviceLocationMenu: {
    bodyElement: i18n.t("locateDevice.helpBody", { ns: "statusMap" }),
    titleElement: i18n.t("locateDevice.title", { ns: "statusMap" }),
  },
  deviceNotificationRules: {
    bodyElement: i18n.t("createNotificationHelpBodyText", {
      ns: "deviceNotifications",
    }),

    titleElement: i18n.t("aboutCustomNotifications", {
      ns: "deviceNotifications",
    }),
  },
  deviceProfileLanding: {
    bodyElement: i18n.t("helpBody", { ns: "deviceProfile" }),
    titleElement: i18n.t("title", { ns: "deviceProfile" }),
  },
  deviceSchedulesList: {
    bodyElement: i18n.t("helpBody", { ns: "schedules" }),
    titleElement: i18n.t("helpTitle", { ns: "schedules" }),
  },
  devices: {
    bodyElement: i18n.t("deviceRosterHelpContentBodyText", { ns: "devices" }),
    titleElement: i18n.t("deviceRosterHelpContentTitle", { ns: "devices" }),
  },
  devicesRoster: {
    bodyElement: i18n.t("deviceRosterHelpContentBodyText", { ns: "devices" }),
    titleElement: i18n.t("deviceRosterHelpContentTitle", { ns: "devices" }),
  },
  editSensorValue: null,
  farmJoinCode: {
    bodyElement: i18n.t("farmJoinCodeHelpSummary", { ns: "farmJoinCodes" }),
    titleElement: i18n.t("farmJoinCodes:aboutFarmJoinCodes"),
  },
  farmUsersList: {
    bodyElement: i18n.t("farmUsersListHelpBodyText", { ns: "farmUsers" }),
    titleElement: i18n.t("farmUsersListHelpTitle", { ns: "farmUsers" }),
  },
  fieldIrrigationHistory: null,
  fieldProfileLanding: null,
  fields: null,
  fieldsRoster: {
    bodyElement: i18n.t("fieldsRosterHelpDescription", { ns: "fields" }),
    titleElement: i18n.t("fieldsRosterTitle", { ns: "fields" }),
  },
  joinFarmWithCode: {
    bodyElement: i18n.t("joinFarmWithCodeHelpBodyText", { ns: "noFarms" }),
    titleElement: i18n.t("joinFarmWithCodeHelpTitle", { ns: "noFarms" }),
  },
  accountDeactivated: null,
  loadError: null,
  manageDeviceSensor: null,
  manageNotificationsByDevice: null,
  notificationPreferences: null,
  manageUserPermissions: {
    bodyElement: i18n.t("farmUserPermissionsHelpSummary", {
      ns: "farmUserPermissions",
    }),
    titleElement: i18n.t("aboutFarmUserPermissions", {
      ns: "farmUserPermissions",
    }),
  },
  more: null,
  noFarms: null,
  notFound: null,
  passwordReset: null,
  phoneNumbersList: {
    bodyElement: i18n.t("phoneNumbersHelpBody", { ns: "phoneNumbers" }),
    titleElement: i18n.t("phoneNumbersHelpTitle", { ns: "phoneNumbers" }),
  },
  pivotBuilder: null,
  pivotControl: null,
  planBuilder: null,
  placeholder: null,
  quickActions: null,
  schedules: null,
  schedulesList: null,
  scratch: null,
  seedDatabase: null,
  selectedDeviceOptions: null,
  setCenterPivotPath: null,
  setDeviceLocationPermanent: {
    bodyElement: (
      <Box flex={1}>
        <Column space={"$4"}>
          <AlertDialogBodyText>
            {i18n.t("pinningExplanation", {
              ns: "setDeviceLocationPermanent",
            })}
          </AlertDialogBodyText>
          <TextWithIcon IconComponent={"Dot"} alignItems={"baseline"}>
            {i18n.t("setDeviceLocationPermanent:locationPinnedExplanation")}
          </TextWithIcon>
          <TextWithIcon IconComponent={"Dot"} alignItems={"flex-start"}>
            {i18n.t("setDeviceLocationPermanent:locationUnpinnedExplanation")}
          </TextWithIcon>
        </Column>
      </Box>
    ),
    titleElement: i18n.t("title", { ns: "setDeviceLocationPermanent" }),
  },
  setFieldBoundary: null,
  setFieldLabelRotation: null,
  setFieldName: null,
  setFieldRowDirection: null,
  setLinearPath: null,
  setReelDistance: null,
  setReelRunDirection: null,
  setVfdSpeed: null,
  shareFeeback: null,
  signIn: null,
  signOut: null,
  signUp: null,
  statusMap: null,
  supportDocumentation: null,
  timeline: null,
  transferFarmOwnership: null,
  unknownFields: null,
  userEmailDisplay: null,
  userPreferences: null,
  verifyInitialPhoneNumber: null,
  verifyPhoneNumber: {
    bodyElement: i18n.t("verifyHelpBodyText", { ns: "phoneNumbers" }),
    titleElement: i18n.t("verifyHelpTitle", { ns: "phoneNumbers" }),
  },
  viewFarmUserPermissions: {
    bodyElement: i18n.t("farmUserPermissionsHelpSummary", {
      ns: "farmUserPermissions",
    }),
    titleElement: i18n.t("aboutFarmUserPermissions", {
      ns: "farmUserPermissions",
    }),
  },
  viewTermsOfService: null,
}

HELP_CONTENT_MAP satisfies HelpContentMap

export function getHelpContent(props: HelpContentProps) {
  if (typeof props.content === "string") {
    return HELP_CONTENT_MAP[props.content] ?? null
  } else if (props.content) {
    return props.content
  }
  return null
}
